import { BellIcon } from 'lucide-react'
import { toast } from 'sonner'
import { Link, useNavigate } from 'react-router'

import { headerActionClassName } from '@/components/layout/header-action'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { formatRelative } from '@/lib/format'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { cn } from 'cn'

import { markAllNotificationsRead, markNotificationRead } from '../commands'
import { selectMyNotifications, selectUnreadCount } from '../selectors'
import type { Notification } from '../types'
import { notificationStyle } from './notification-style'

export function NotificationsMenu() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const notifications = useAppSelector(selectMyNotifications)
  const unreadCount = useAppSelector(selectUnreadCount)

  /** Marks the notification read first, then opens its target; the page guards decide whether it is allowed. */
  async function open(notification: Notification) {
    if (!notification.readAt) {
      const result = await dispatch(markNotificationRead(notification.id))
      if (!result.ok) {
        toast.error(result.error)
        return
      }
    }
    navigate(notification.route)
  }

  async function markAll() {
    const result = await dispatch(markAllNotificationsRead())
    if (!result.ok) toast.error(result.error)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={headerActionClassName}
          aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        >
          <BellIcon />
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground tabular-nums ring-2 ring-background">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[min(22rem,calc(100vw-2rem))] p-0">
        <div className="flex items-center justify-between px-4 py-3">
          <p className="text-sm font-semibold">Notifications</p>
          {unreadCount > 0 && (
            <button type="button" onClick={markAll} className="text-xs font-medium text-primary hover:underline">
              Mark all as read
            </button>
          )}
        </div>
        <DropdownMenuSeparator className="m-0" />

        <div className="max-h-96 overflow-y-auto p-1">
          {notifications.length ? (
            notifications.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} onSelect={open} />
            ))
          ) : (
            <p className="px-4 py-10 text-center text-sm text-text-tertiary">You&apos;re all caught up.</p>
          )}
        </div>
        <DropdownMenuSeparator className="m-0" />
        <Link to="/notifications" className="block px-4 py-2.5 text-center text-xs font-medium text-primary hover:underline">
          View all notifications
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function NotificationItem({
  notification,
  onSelect,
}: {
  notification: Notification
  onSelect: (notification: Notification) => void
}) {
  const { icon: Icon, className } = notificationStyle(notification.type)
  const unread = !notification.readAt

  return (
    <DropdownMenuItem onSelect={() => void onSelect(notification)} className="items-start gap-3 px-3 py-2.5">
      <span className={cn('mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full', className)}>
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1 space-y-0.5">
        <span className="flex items-center gap-2">
          <span className={cn('truncate text-sm', unread ? 'font-semibold' : 'font-medium')}>{notification.title}</span>
          {unread && <span aria-label="Unread" className="size-2 shrink-0 rounded-full bg-primary" />}
        </span>
        <span className="line-clamp-2 block text-xs text-text-secondary">{notification.message}</span>
        <span className="block text-[11px] text-text-tertiary">{formatRelative(notification.createdAt)}</span>
      </span>
    </DropdownMenuItem>
  )
}
